import { getOrganizationLogo, CARD_CODES, CardCodes } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { Modal, ModalProps, Tag, Typography } from 'antd';

import { ScrapeSpecForCard } from '~apis/card';
import {
  useGetScrapeSpecForBilling,
  useGetScrapeSpecForPrePayments,
} from '~hooks/queries/card/query';

interface Props extends ModalProps {}

const getInfoText = (availableDateRange: number | null, type: 'billing' | 'prepayment') => {
  if (availableDateRange === null) {
    return `${type === 'billing' ? '청구' : '즉시결제'}내역 연결 미제공`;
  }
  // 3년(36게월) 이상은 최근 3년
  const dateText =
    availableDateRange >= 36
      ? `수집일 기준 최근 ${Math.floor(availableDateRange / 12)}년`
      : `수집일 기준 최근 ${availableDateRange}개월`;
  return dateText;
};

const CardInfoItem = ({
  organizationCode,
  scrapeForBilling,
  scrapeForPrepayment,
}: {
  organizationCode: CardCodes;
  scrapeForBilling?: ScrapeSpecForCard;
  scrapeForPrepayment?: ScrapeSpecForCard;
}) => {
  const isConnected = scrapeForBilling?.isConnected && scrapeForPrepayment?.isConnected;
  return (
    <Flex
      style={{
        padding: '7px 16px',
        borderRadius: 6,
        border: `1px solid ${
          isConnected ? colorTokens.textPrimaryAccent : colorTokens.borderDefault
        }`,
      }}
      justify="space-between"
      align="center"
    >
      <Flex
        gap={16}
        align="center"
        style={{
          width: 160,
        }}
      >
        <div>
          <img
            src={getOrganizationLogo(organizationCode).x1}
            alt={CARD_CODES[organizationCode]}
            style={{
              width: 24,
              height: 34,
            }}
          />
        </div>
        <Typography.Text>{CARD_CODES[organizationCode]}</Typography.Text>
        {isConnected ? (
          <Tag
            color="cyan"
            style={{
              margin: 0,
            }}
          >
            연동
          </Tag>
        ) : (
          <div
            style={{
              width: 33,
            }}
          />
        )}
      </Flex>
      <Typography
        style={{
          width: 160,
          color:
            scrapeForBilling?.isConnected && scrapeForBilling?.maxInquiryPeriodInMonth !== null
              ? colorTokens.textPrimaryAccent
              : colorTokens.textDescription,
        }}
      >
        {getInfoText(scrapeForBilling?.maxInquiryPeriodInMonth ?? null, 'billing')}
      </Typography>
      <Typography
        style={{
          width: 160,
          color:
            scrapeForPrepayment?.isConnected &&
            scrapeForPrepayment?.maxInquiryPeriodInMonth !== null
              ? colorTokens.textPrimaryAccent
              : colorTokens.textDescription,
        }}
      >
        {getInfoText(scrapeForPrepayment?.maxInquiryPeriodInMonth ?? null, 'prepayment')}
      </Typography>
    </Flex>
  );
};

const CardAvailableDateInfoModal: React.FC<Props> = ({ ...props }) => {
  const { data } = useGetScrapeSpecForBilling();
  const { data: prepaymentData } = useGetScrapeSpecForPrePayments();

  const specForBilling = data?.Data?.data?.items || [];
  const specForPrepayment = prepaymentData?.Data?.data?.items || [];

  const cardList = Object.keys(CARD_CODES) as CardCodes[];
  return (
    <Modal
      title="카드사별 조회가능기간"
      {...props}
      centered
      footer={null}
      width={600}
      bodyStyle={{
        maxHeight: '80vh',
        overflow: 'auto',
      }}
    >
      <Flex gap={24} direction="column">
        <Typography>
          아래는 각 카드사 웹사이트에서 청구내역 조회가 가능한 기간이며, 클로브에서 카드 연동을
          유지하실 경우 수집 데이터가 누적되어 더 긴 기간을 조회하실 수 있습니다.{' '}
        </Typography>
        <Flex gap={12} direction="column">
          <Flex
            gap={16}
            align="center"
            justify="space-between"
            style={{
              padding: '7px 16px',
            }}
          >
            <div
              style={{
                width: 160,
              }}
            ></div>
            <Typography.Text
              style={{
                width: 160,
                color: colorTokens.textDescription,
              }}
            >
              청구내역 조회 기간
            </Typography.Text>
            <Typography.Text
              style={{
                width: 160,
                color: colorTokens.textDescription,
              }}
            >
              즉시결제내역 조회 기간
            </Typography.Text>
          </Flex>
          {cardList
            .sort(
              // 연동된것 위로
              (a, b) =>
                Number(specForBilling.find((spec) => spec.organizationCode === b)?.isConnected) -
                Number(specForBilling.find((spec) => spec.organizationCode === a)?.isConnected),
            )
            .map((card) => {
              const targetScrapeForBilling = specForBilling.find(
                (spec) => spec.organizationCode === card,
              );
              const targetScrapeForPrepayment = specForPrepayment.find(
                (spec) => spec.organizationCode === card,
              );

              return (
                <CardInfoItem
                  key={card}
                  organizationCode={card}
                  scrapeForBilling={targetScrapeForBilling}
                  scrapeForPrepayment={targetScrapeForPrepayment}
                />
              );
            })}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default CardAvailableDateInfoModal;
